// JumbotronHero.jsx
import React from "react";
import "./Styles.css";
import { useWindowWidth } from "@react-hook/window-size";

/**
 * JumbotronHero
 * - Enterprise-grade hero with a tinted background image, noise, orbs, and subtle scan/grid accents.
 * - No libraries. Pure JSX + CSS.
 *
 * Usage:
 * <JumbotronHero
 *   kicker="U.S. MARKET • PROMOTIONAL SWEEPSTAKES INFRASTRUCTURE"
 *   title={<>Promotional Sweepstakes <span className="jh-accent">Infrastructure</span> — Built for <span className="jh-accent2">Compliance</span> and Control.</>}
 *   subtitle="Infrastructure designed to support legally structured dual-currency promotional gaming models in permitted jurisdictions — with tenant-scoped controls, governed rulesets, and audit-grade exports."
 *   primaryCta={{ label: "Request Infrastructure Demo", href: "/book-demo" }}
 *   secondaryCta={{ label: "Download Compliance Overview", href: "/compliance.pdf" }}
 *   checklistTitle="Infrastructure Capabilities"
 *   checklist={[
 *     "Tenant-scoped policy enforcement & audit exports",
 *     "Ruleset versioning + jurisdiction tagging",
 *     "Deterministic verification artifacts (provably fair)",
 *     "Dual-ledger accounting separation (configurable)",
 *     "AMOE intake tracking + traceable workflows",
 *   ]}
 *   bgImage="/assets/images/hero/blueprint-bg.jpg"
 * />
 */

const JumbotronHero = ({
  kicker = "ENTERPRISE INFRASTRUCTURE PLATFORM",
  title = (
    <>
      Multi-Tenant Casino Infrastructure{" "}
      <span className="jh-accent">built for operator control</span>.
    </>
  ),
  subtitle = "Ethosino is a production-grade, white-label multi-tenant engine designed for operators who demand provable fairness, tenant isolation, and enterprise-grade governance — without third-party game dependencies.",
  primaryCta = { label: "Request Live Demo", href: "/book-demo" },
  secondaryCta = { label: "View Architecture", href: "/architecture" },
  checklistTitle = "Operator Fit Checklist",
  checklist = [
    "Need tenant isolation with scoped configuration",
    "Want exportable audit trails + admin action logs",
    "Require deterministic replay verification artifacts",
    "Prefer modular integrations (payments, KYC, analytics)",
    "Need governed rulesets and versioned controls",
  ],
  note = "Note: This website describes infrastructure capabilities. Operators are responsible for jurisdictional compliance and should work with qualified counsel.",
  bgImage = "/assets/images/hero/sweeps.png",
  rightCardTopTags = ["Compliance-oriented", "Operator controls", "Audit artifacts"],
}) => {
  const width = useWindowWidth();
  return (
    <section
      className="jh-wrap"
      style={width <= 775 ? { ["--jh-bg-url"]: `url(${bgImage})`, paddingTop: 22.25, paddingBottom: 22.25 } : { ["--jh-bg-url"]: `url(${bgImage})`, paddingTop: 275, paddingBottom: 200 }}
      aria-label="Enterprise jumbotron hero"
    >
      {/* Background Layers */}
      <div className="jh-bg" aria-hidden="true">
        <div className="jh-bgImage" />
        <div className="jh-tint" />
        <div className="jh-orbs" />
        <div className="jh-grid" />
        <div className="jh-scan" />
        <div className="jh-noise" />
        <div className="jh-vignette" />
      </div>

      <div className="jh-container">
        <div className="jh-inner">
          {/* Left Content */}
          <div className="jh-left">
            <div className="jh-kickerRow">
              <span className="jh-dot" />
              <span className="jh-kicker">{kicker}</span>
            </div>

            <h1 className="jh-title">
              {title}
              <span className="jh-underline" aria-hidden="true" />
            </h1>

            <p className="jh-subtitle">{subtitle}</p>

            <div className="jh-ctaRow">
              <a className="jh-btn jh-btnPrimary" href={primaryCta?.href}>
                {primaryCta?.label}
                <span className="jh-btnIcon" aria-hidden="true">
                  →
                </span>
              </a>

              <a className="jh-btn jh-btnGhost" href={secondaryCta?.href}>
                {secondaryCta?.label}
                <span className="jh-btnIcon" aria-hidden="true">
                  →
                </span>
              </a>
            </div>

            <div className="jh-miniGrid" role="list" aria-label="Key highlights">
              <div className="jh-miniCard" role="listitem">
                <div className="jh-miniLabel">TENANT ISOLATION</div>
                <div className="jh-miniValue">Per-operator security perimeter</div>
              </div>
              <div className="jh-miniCard" role="listitem">
                <div className="jh-miniLabel">PROVABLY FAIR</div>
                <div className="jh-miniValue">Seed lifecycle + replay verifier</div>
              </div>
              <div className="jh-miniCard" role="listitem">
                <div className="jh-miniLabel">COMPLIANCE</div>
                <div className="jh-miniValue">Audit-grade logs & exports</div>
              </div>
              <div className="jh-miniCard" role="listitem">
                <div className="jh-miniLabel">SCALING</div>
                <div className="jh-miniValue">Horizontal services + partitioning</div>
              </div>
            </div>

            <div className="jh-note">
              <span className="jh-noteIcon" aria-hidden="true">
                ⚑
              </span>
              <span>{note}</span>
            </div>
          </div>

          {/* Right Content */}
          <aside className="jh-right" aria-label="Hero checklist panel">
            <div className="jh-card">
              <div className="jh-cardTop">
                <div className="jh-chipRow" aria-label="Capability tags">
                  {rightCardTopTags?.slice(0, 3).map((t) => (
                    <span className="jh-chip" key={t}>
                      {t}
                    </span>
                  ))}
                </div>
                <div className="jh-cardTitle">{checklistTitle}</div>
                <div className="jh-cardDesc">
                  A quick way to validate whether Ethosino’s infrastructure model matches your operational constraints and governance requirements.
                </div>
              </div>

              <ul className="jh-checklist">
                {checklist?.map((item, idx) => (
                  <li key={`${item}-${idx}`} className="jh-checkItem">
                    <span className="jh-checkIcon" aria-hidden="true">
                      ✓
                    </span>
                    <span className="jh-checkText">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="jh-cardActions">
                <a className="jh-miniBtn" href="/docs">
                  Read technical docs <span aria-hidden="true">→</span>
                </a>
                <a className="jh-miniBtn" href="/walkthrough">
                  Architecture walkthrough <span aria-hidden="true">→</span>
                </a>
              </div>

              <div className="jh-cardFooter">
                <div className="jh-footLine">
                  <span className="jh-footKey">Proof-ready:</span> deterministic artifacts, export bundles, and tenant-scoped controls.
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default JumbotronHero;
